import { useEffect, useState } from "react";
import { FileText, CheckCircle2, ExternalLink, Download, FileSignature } from "lucide-react";
import { http, errMsg, fmtDateTime, downloadFile } from "../../api";
import { SignaturePad } from "../AgreementSignModal";

export default function AgreementsStep({ onChanged }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [sig, setSig] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => http.get("/creator/agreements").then(r => { setData(r.data); setActive(r.data.items.find(a => !a.signed) || null); setSig(""); setAccepted(false); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openPdf = async id => {
    const { data: blob } = await http.get(`/creator/agreements/${id}/file`, { responseType: "blob" });
    window.open(URL.createObjectURL(blob), "_blank");
  };
  const sign = async () => {
    setBusy(true); setErr("");
    try { await http.post(`/creator/agreements/${active.id}/sign`, { signature: sig, accepted }); await load(); onChanged(); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return err ? <div className="notice error">{err}</div> : <div className="loading">Loading agreements…</div>;
  return (
    <section className="form-card" data-testid="creator-agreements-step">
      <h2>Step 4 · Sign agreements</h2>
      <p>You are shortlisted. To start working with Vocalhouse, read and digitally sign the Non-Disclosure Agreement and the Freelancer Agreement. Your signature, name, date, time and IP are stamped on every page.</p>
      {data.items.length === 0 && <div className="empty" data-testid="agreements-empty">The Vocalhouse team has not uploaded the agreements yet. You will be able to sign them here soon.</div>}
      <div className="list-rows" style={{ marginBottom: 14 }}>
        {data.items.map(a => (
          <div key={a.id} className={`list-row ${active?.id === a.id ? "active-border" : ""}`} data-testid={`agreement-item-${a.id}`}>
            <FileText size={18} color={a.signed ? "var(--success)" : "var(--primary)"} />
            <div className="list-row-info"><strong>{a.title}</strong><p>{a.kind_label} · {a.pages} page(s){a.description ? ` · ${a.description}` : ""}{a.signed ? ` · Signed ${fmtDateTime(a.signed_at)}` : ""}</p></div>
            <div className="list-row-actions">
              {a.signed
                ? <><button className="ghost" onClick={() => downloadFile(`/creator/agreements/signed/${a.signed_id}/file`, {}, `${a.title} - signed.pdf`)} data-testid={`agreement-signed-copy-${a.id}`}><Download size={13} /> Signed copy</button><span className="badge success"><CheckCircle2 size={12} /> Signed</span></>
                : <><button className="ghost" onClick={() => openPdf(a.id)} data-testid={`agreement-open-${a.id}`}><ExternalLink size={13} /> Read PDF</button><button className="pill-action neutral" onClick={() => { setActive(a); setSig(""); setAccepted(false); }} data-testid={`agreement-select-${a.id}`}>Sign</button></>}
            </div>
          </div>
        ))}
      </div>
      {active && (
        <div className="card" data-testid="agreement-sign-area">
          <div className="card-head"><h2><FileSignature size={16} /> Sign: {active.title}</h2></div>
          <p className="muted" style={{ marginBottom: 10 }}>Read the PDF first, then draw your signature below.</p>
          <SignaturePad onChange={setSig} />
          <label className={`checkbox-label ${accepted ? "checked" : ""}`} style={{ marginTop: 12, display: "inline-flex" }} data-testid="agreement-accept">
            <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} /> I have read and agree to the terms of "{active.title}"
          </label>
          {err && <div className="notice error" style={{ marginTop: 10 }}>{err}</div>}
          <button className="primary" style={{ marginTop: 12 }} disabled={busy || !sig || !accepted} onClick={sign} data-testid="agreement-submit">{busy ? "Stamping signature…" : "Sign agreement"}</button>
        </div>
      )}
      {data.items.length > 0 && data.all_signed && <div className="notice success" data-testid="agreements-all-signed"><CheckCircle2 size={15} /> All agreements signed. Welcome aboard — the Vocalhouse team will share training details with you.</div>}
    </section>
  );
}
